<template>
    <div class="quick-reply-container">
        <div
            ref="scrollContainer"
            class="quick-reply-scroll"
            @mousedown="startDrag"
            @mousemove="onDrag"
            @mouseup="endDrag"
            @mouseleave="endDrag"
        >
            <button
                v-for="(option, index) in options"
                :key="index"
                class="quick-reply-btn"
                :class="{ 'is-freeform': option.is_freeform }"
                @click="handleClick(option, $event)"
            >
                <v-icon
                    v-if="option.icon"
                    small
                    class="quick-reply-icon"
                >{{ option.icon }}</v-icon>
                <span>{{ option.label }}</span>
            </button>
        </div>

        <transition name="freeform-fade">
            <div
                v-if="show_freeform_input"
                class="freeform-input-container"
            >
                <input
                    ref="freeformInput"
                    v-model="freeform_text"
                    type="text"
                    class="freeform-input"
                    :placeholder="freeform_placeholder"
                    @keyup.enter="submitFreeform"
                />
                <button
                    class="freeform-send-btn"
                    :disabled="!freeform_text.trim()"
                    @click="submitFreeform"
                >
                    <v-icon small>mdi-send</v-icon>
                </button>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'QuickReplyButtons',

    props: {
        options: {
            type: Array,
            required: true,
            validator: (options) => {
                return options.every(opt => opt.label);
            }
        },
        freeform_placeholder: {
            type: String,
            default: 'Type your message...'
        }
    },

    data() {
        return {
            show_freeform_input: false,
            freeform_text: '',
            is_dragging: false,
            drag_start_x: 0,
            scroll_start: 0,
            has_dragged: false
        };
    },

    methods: {
        startDrag(e) {
            this.is_dragging = true;
            this.has_dragged = false;
            this.drag_start_x = e.pageX;
            this.scroll_start = this.$refs.scrollContainer.scrollLeft;
            this.$refs.scrollContainer.style.cursor = 'grabbing';
        },

        onDrag(e) {
            if (!this.is_dragging) return;
            e.preventDefault();
            const delta = e.pageX - this.drag_start_x;
            if (Math.abs(delta) > 5) {
                this.has_dragged = true;
            }
            this.$refs.scrollContainer.scrollLeft = this.scroll_start - delta;
        },

        endDrag() {
            this.is_dragging = false;
            if (this.$refs.scrollContainer) {
                this.$refs.scrollContainer.style.cursor = 'grab';
            }
        },

        handleClick(option, event) {
            if (this.has_dragged) {
                event.preventDefault();
                return;
            }
            if (option.is_freeform) {
                this.show_freeform_input = true;
                this.$nextTick(() => {
                    this.$refs.freeformInput?.focus();
                });
                return;
            }
            this.$emit('select', option);
        },

        submitFreeform() {
            const text = this.freeform_text.trim();
            if (!text) return;

            this.$emit('freeform-submit', text);
            this.freeform_text = '';
            this.show_freeform_input = false;
        },

        reset() {
            this.show_freeform_input = false;
            this.freeform_text = '';
        }
    }
};
</script>

<style lang="scss" scoped>
.quick-reply {
    &-container {
        width: 100%;
    }

    &-scroll {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 0.5rem 0;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    &-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.6rem 1rem;
        background: #fff;
        border: 1.5px solid var(--brand-primary);
        border-radius: 1.5rem;
        color: var(--brand-primary);
        font-size: 0.85rem;
        font-weight: 500;
        white-space: nowrap;
        cursor: pointer;
        transition: all 0.2s ease;
        flex-shrink: 0;

        .quick-reply-icon {
            color: var(--brand-primary);
        }

        &:hover {
            background: var(--brand-primary);
            color: #fff;

            .quick-reply-icon {
                color: #fff;
            }
        }

        &:active {
            transform: scale(0.97);
        }

        &.is-freeform {
            background: transparent;
            border-style: dashed;

            &:hover {
                background: #f5f5f5;
                color: var(--brand-primary);
                border-color: var(--brand-primary);

                .quick-reply-icon {
                    color: var(--brand-primary);
                }
            }
        }
    }

    &-icon {
        color: var(--brand-primary);
        transition: color 0.2s ease;
    }
}

.freeform {
    &-input-container {
        display: flex;
        gap: 0.5rem;
        margin-top: 0.25rem;
        padding: 0.5rem;
        background: #f9f9f9;
        border-radius: 1.5rem;
        border: 1px solid #e0e0e0;
    }

    &-input {
        flex: 1;
        border: none;
        background: transparent;
        padding: 0 0.55rem;
        font-size: 0.9rem;
        outline: none;

        &::placeholder {
            color: #aaa;
        }
    }

    &-send-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        border: none;
        border-radius: 50%;
        background: var(--brand-primary);
        color: #fff;
        cursor: pointer;
        transition: all 0.2s ease;

        .v-icon {
            color: #fff;
        }
        
        &:hover:not(:disabled) {
            background: var(--brand-primary-dark);
        }
        
        &:disabled {
            background: #ccc;
        }
    }

    &-fade-enter-active,
    &-fade-leave-active {
        transition: all 0.25s ease;
    }
    
    &-fade-enter,
    &-fade-leave-to {
        opacity: 0;
        transform: translateY(-0.5rem);
    }
}
</style>
